import React, { useState, useEffect } from 'react';
import { useTranslation } from './i18n';

export default function UnsubscribePage({ onNavigate }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState('loading'); // loading, success, error, already
  const [message, setMessage] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (!token) {
      setStatus('error');
      setMessage(t('unsubscribe.invalid_link'));
      return;
    }

    const API_BASE = process.env.REACT_APP_CANDIDATE_API || 'http://127.0.0.1:8002';

    fetch(`${API_BASE}/api/email-preferences`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, enabled: false }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (res.ok) {
          if (data.already_unsubscribed) {
            setStatus('already');
            setMessage(t('unsubscribe.already_message'));
          } else {
            setStatus('success');
            setMessage(t('unsubscribe.success_message'));
          }
        } else {
          setStatus('error');
          setMessage(data.detail || t('unsubscribe.generic_error'));
        }
      })
      .catch(() => {
        setStatus('error');
        setMessage(t('unsubscribe.network_error'));
      });
  }, [t]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logozondertekst.png" alt="Logo" className="h-8" />
            <span className="font-bold text-gray-900">Betaaldsolliciteren.nl</span>
          </div>
          <button
            onClick={() => onNavigate('/')}
            className="text-gray-600 hover:text-gray-900 transition-colors"
          >
            {t('unsubscribe.back_home')}
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-md w-full text-center">
          {status === 'loading' && (
            <div>
              <div className="w-12 h-12 border-4 border-brand-green border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
              <p className="text-gray-600 text-lg">{t('unsubscribe.loading')}</p>
            </div>
          )}

          {status === 'success' && (
            <div className="bg-white rounded-2xl shadow-sm p-8 border border-gray-200">
              <div className="text-5xl mb-4">✅</div>
              <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('unsubscribe.success_title')}</h1>
              <p className="text-gray-600 mb-6">{message}</p>
              <p className="text-gray-500 text-sm mb-8">
                {t('unsubscribe.resubscribe_hint')}
              </p>
              <button
                onClick={() => onNavigate('/')}
                className="px-6 py-3 bg-brand-green text-white font-bold rounded-xl hover:opacity-90 transition-opacity"
              >
                {t('unsubscribe.homepage_button')}
              </button>
            </div>
          )}

          {status === 'already' && (
            <div className="bg-white rounded-2xl shadow-sm p-8 border border-gray-200">
              <div className="text-5xl mb-4">📧</div>
              <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('unsubscribe.already_title')}</h1>
              <p className="text-gray-600 mb-6">{message}</p>
              <p className="text-gray-500 text-sm mb-8">
                {t('unsubscribe.resubscribe_hint')}
              </p>
              <button
                onClick={() => onNavigate('/')}
                className="px-6 py-3 bg-brand-green text-white font-bold rounded-xl hover:opacity-90 transition-opacity"
              >
                {t('unsubscribe.homepage_button')}
              </button>
            </div>
          )}

          {status === 'error' && (
            <div className="bg-white rounded-2xl shadow-sm p-8 border border-red-200">
              <div className="text-5xl mb-4">⚠️</div>
              <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('unsubscribe.error_title')}</h1>
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="space-y-3">
                <button
                  onClick={() => onNavigate('/')}
                  className="w-full px-6 py-3 bg-brand-green text-white font-bold rounded-xl hover:opacity-90 transition-opacity"
                >
                  {t('unsubscribe.homepage_button')}
                </button>
                <a
                  href="mailto:info@betaaldsolliciteren.nl"
                  className="block text-gray-500 hover:text-gray-700 text-sm transition-colors"
                >
                  {t('unsubscribe.help_mail')}
                </a>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
